import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, get, set } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import { logAdminAction } from '../../firebase/auditLogger';

function AdminSettings() {
  const [settings, setSettings] = useState({
    maintenanceMode: 'off',
    registrationEnabled: true,
    adminRegistrationEnabled: false,
    dataRetentionDays: '90',
    defaultEmbedTheme: 'dark',
    supportEmail: 'support@devtracker.app',
    platformName: 'DevTracker'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { userProfile } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const snap = await get(ref(db, 'settings'));
        if (snap.exists()) {
          const val = snap.val();
          setSettings({
            maintenanceMode: val.maintenanceMode === true ? 'profiles' : (val.maintenanceMode === false ? 'off' : (val.maintenanceMode || 'off')),
            registrationEnabled: val.registrationEnabled !== undefined ? val.registrationEnabled : true,
            adminRegistrationEnabled: val.adminRegistrationEnabled !== undefined ? val.adminRegistrationEnabled : false,
            dataRetentionDays: val.dataRetentionDays || '90',
            defaultEmbedTheme: val.defaultEmbedTheme || 'dark',
            supportEmail: val.supportEmail || 'support@devtracker.app',
            platformName: val.platformName || 'DevTracker'
          });
        }
      } catch (e) {
        console.error("Error loading settings:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings(prev => ({ ...prev, [e.target.name]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await set(ref(db, 'settings'), settings);
      logAdminAction(userProfile?.adminId, `Updated Global Platform Settings`, 'global_settings');
      alert("Global platform settings updated successfully.");
    } catch (e) {
      alert("Error saving settings: " + e.message);
    }
    setSaving(false);
  };

  if (loading) return <div style={{color: '#00ffae', padding: '100px', textAlign: 'center'}}>Loading Configuration Core...</div>;

  return (
    <div className="animate-up" style={{ maxWidth: '900px' }}>
      <div className="admin-page-header">
        <h1>Platform Settings</h1>
        <p>Manage application-wide configurations, security keys, and data policies.</p>
      </div>

      <div style={{display: 'flex', flexDirection: 'column', gap: '30px', marginBottom: '40px'}}>
        
        {/* GENERAL CONFIGURATION */}
        <div className="admin-card">
          <h3 style={{color: '#fff', marginBottom: '25px', fontSize: '1.1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '15px'}}>General Configuration</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
              <div>
                <label style={{display: 'block', color: '#8b949e', fontSize: '0.9rem', marginBottom: '8px'}}>Platform Name</label>
                <input type="text" name="platformName" value={settings.platformName} onChange={handleChange} style={{width: '100%', padding: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px'}} />
              </div>
              <div>
                <label style={{display: 'block', color: '#8b949e', fontSize: '0.9rem', marginBottom: '8px'}}>Support Email Router</label>
                <input type="email" name="supportEmail" value={settings.supportEmail} onChange={handleChange} style={{width: '100%', padding: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px'}} />
              </div>
            </div>
          </div>
        </div>

        {/* SECURITY & ACCESS */}
        <div className="admin-card">
          <h3 style={{color: '#fff', marginBottom: '25px', fontSize: '1.1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '15px'}}>Security & Access</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
            
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px'}}>
              <div style={{marginRight: '20px'}}>
                <strong style={{color: '#fff', display: 'block', marginBottom: '5px'}}><i className="fa-solid fa-user-plus"></i> Public Registration</strong>
                <span style={{color: '#8b949e', fontSize: '0.85rem'}}>Allow new users to sign up for the platform. Disable during invite-only phases.</span>
              </div>
              <label className="switch" style={{position: 'relative', display: 'inline-block', width: '50px', height: '26px'}}>
                <input type="checkbox" name="registrationEnabled" checked={settings.registrationEnabled} onChange={handleChange} style={{opacity: 0, width: 0, height: 0}} />
                <span className="slider round" style={{position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: settings.registrationEnabled ? '#00ffae' : '#ff375f', transition: '.4s', borderRadius: '34px'}}>
                  <span style={{position: 'absolute', content: '""', height: '18px', width: '18px', left: settings.registrationEnabled ? '28px' : '4px', bottom: '4px', backgroundColor: '#0d1117', transition: '.4s', borderRadius: '50%'}}></span>
                </span>
              </label>
            </div>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', background: 'rgba(255, 55, 95, 0.05)', border: '1px solid rgba(255, 55, 95, 0.1)', borderRadius: '8px', marginTop: '10px'}}>
              <div style={{marginRight: '20px'}}>
                <strong style={{color: '#ff375f', display: 'block', marginBottom: '5px'}}><i className="fa-solid fa-vault"></i> Public Admin Registration</strong>
                <span style={{color: '#8b949e', fontSize: '0.85rem'}}>Allow new administrative accounts to be created. Keep locked (Off) unless actively recruiting.</span>
              </div>
              <label className="switch" style={{position: 'relative', display: 'inline-block', width: '50px', height: '26px'}}>
                <input type="checkbox" name="adminRegistrationEnabled" checked={settings.adminRegistrationEnabled} onChange={handleChange} style={{opacity: 0, width: 0, height: 0}} />
                <span className="slider round" style={{position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: settings.adminRegistrationEnabled ? '#00ffae' : '#ff375f', transition: '.4s', borderRadius: '34px'}}>
                  <span style={{position: 'absolute', content: '""', height: '18px', width: '18px', left: settings.adminRegistrationEnabled ? '28px' : '4px', bottom: '4px', backgroundColor: '#0d1117', transition: '.4s', borderRadius: '50%'}}></span>
                </span>
              </label>
            </div>
            
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', background: 'rgba(255,161,22,0.1)', border: '1px solid rgba(255,161,22,0.2)', borderRadius: '8px', marginTop: '10px'}}>
              <div style={{marginRight: '20px'}}>
                <strong style={{color: '#ffa116', display: 'block', marginBottom: '5px'}}><i className="fa-solid fa-triangle-exclamation"></i> Maintenance Mode Level</strong>
                <span style={{color: '#8b949e', fontSize: '0.85rem'}}>Select which parts of the application should be blocked from public access.</span>
              </div>
              <select name="maintenanceMode" value={settings.maintenanceMode} onChange={handleChange} style={{padding: '10px', background: '#0d1117', color: settings.maintenanceMode === 'off' ? '#00ffae' : '#ff375f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontWeight: 'bold', minWidth: '150px'}}>
                <option value="off">🟢 Disabled (Live)</option>
                <option value="profiles">🟠 Profiles & Embeds Only</option>
                <option value="all">🔴 Entire Site (Except Admin)</option>
              </select>
            </div>
          </div>
        </div>

        {/* DATA MANAGEMENT & PREFERENCES */}
        <div className="admin-card">
          <h3 style={{color: '#fff', marginBottom: '25px', fontSize: '1.1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '15px'}}>Data Management & Defaults</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
              <div>
                <label style={{display: 'block', color: '#8b949e', fontSize: '0.9rem', marginBottom: '8px'}}>Default Embed Theme</label>
                <select name="defaultEmbedTheme" value={settings.defaultEmbedTheme} onChange={handleChange} style={{width: '100%', padding: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px'}}>
                  <option value="dark">Dark Professional</option>
                  <option value="light">Light Minimalist</option>
                  <option value="system">System Default</option>
                </select>
              </div>
              <div>
                <label style={{display: 'block', color: '#8b949e', fontSize: '0.9rem', marginBottom: '8px'}}>Log Data Retention Policy</label>
                <select name="dataRetentionDays" value={settings.dataRetentionDays} onChange={handleChange} style={{width: '100%', padding: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px'}}>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                  <option value="365">1 Year</option>
                  <option value="forever">Indefinite</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
          <button onClick={saveSettings} disabled={saving} style={{background: 'var(--ai-primary-gradient)', color: '#0d1117', border: 'none', padding: '15px 30px', borderRadius: '10px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', transition: 'transform 0.2s ease'}}>
            {saving ? 'Saving System Core...' : 'Sync Configuration Defaults'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default AdminSettings;
